import React, {ChangeEvent, Component} from "react";
import * as ActionplanData from "./ActionplanData";
import {viewBys, viewByLabels} from "./Controls";
import Form from "react-bootstrap/Form";
import * as _ from "lodash";


interface MultiCheckboxOption {
    label: string
    value: string
    selected?: boolean
}

interface MultiCheckboxProps {
    options: Array<MultiCheckboxOption>
    onChange?: (values: undefined | string[]) => void
}

interface MultiCheckboxState {
    valueMap: Map<string, MultiCheckboxOption>
    expanded: boolean
    search: string
    all: boolean
}


export class MultiCheckboxDropdown extends Component<MultiCheckboxProps, MultiCheckboxState> {
    ref!: HTMLDivElement | null;

    constructor(props: MultiCheckboxProps) {
        super(props);

        const valueMap = new Map<string, MultiCheckboxOption>();
        for (const option of this.props.options) {
            option.selected = true;
            valueMap.set(option.value, option);
        }
        this.state = {
            valueMap: valueMap,
            expanded: false,
            search: "",
            all: true
        };
        this.handleClick = this.handleClick.bind(this);
        this.handleEscape = this.handleEscape.bind(this);
    }

    componentDidMount() {
        document.addEventListener("click", this.handleClick);
        document.addEventListener("keydown", this.handleEscape);
    }

    componentWillUnmount() {
        document.removeEventListener("click", this.handleClick);
        document.removeEventListener("keydown", this.handleEscape);
    }

    close() {
        this.setState({
            expanded: false
        });
    }

    handleClick(event) {
        if (this.state.expanded && this.ref && !this.ref.contains(event.target)) {
            this.close();
        }
    }

    handleEscape(event) {
        if (event.key === "Escape") {
            this.close();
        }
    }

    displayText() {
        let count = 0;
        for (const option of this.props.options) {
            if (option.selected) count++;
        }

        if (count === this.props.options.length) {
            return "All";
        } else if ( count === 0) {
            return "None";
        } else {
            return "One or More Values Selected";
        }
    }

    emitChange() {
        if (this.props.onChange) {
            // Loop through options grabbing all selected
            if (!this.state.all) {
                const values: Array<string> = [];
                for (const option of this.props.options) {
                    if (option.selected) {
                        values.push(option.value);
                    }
                }
                this.props.onChange(values);
            // All is selected, no filters
            } else {
                this.props.onChange(undefined);
            }
        }
    }

    handleAllClick() {
        const newAll = !(this.state.all);
        this.state.valueMap.forEach((value ) => {
            value.selected = newAll;
        });
        this.setState({all: newAll}, () => {this.emitChange();});
    }

    static getDerivedStateFromProps(props: MultiCheckboxProps, state: MultiCheckboxState) {
        const nextState: Partial<MultiCheckboxState> = {};
        const newValueMap = new Map<string, MultiCheckboxOption>();

        // Set values of new properties from existing selects defaulting to all.
        for (const option of props.options) {
            newValueMap.set(option.value, option);
            option.selected = state.valueMap.get(option.value)?.selected || state.all;
        }

        nextState.valueMap = newValueMap;
        return nextState;
    }

    handleOptionChange(ev: ChangeEvent<HTMLInputElement>) {
        let newAll = true;
        const option = this.state.valueMap.get(ev.target.value);

        // Return early if not an option
        if (!option) { return; }

        // Change selected on the option
        option.selected = !option.selected;

        // Determine if the all option should still be checked/unchecked
        this.state.valueMap.forEach((option) => {
            if (!option.selected) {
                newAll = false;
            }
        });

        this.setState({ all: newAll, valueMap: this.state.valueMap }, () => {
            this.emitChange();
        });
    }

    handleSearchChange(ev: ChangeEvent<HTMLInputElement>) {
        this.setState({
            search: ev.target.value.toLowerCase()
        });
    }

    renderOptions() {
        const renderedOptions: Array<JSX.Element> = [];
        const options = Array.from(this.state.valueMap.values());
        for (const option of options) {
            if (option.label.toLowerCase().includes(this.state.search)) {
                renderedOptions.push(
                    <div key={option.value}>
                        <Form.Check type="checkbox"
                                    id={_.uniqueId("multicheckbox-")}
                                    label={option.label}
                                    checked={option.selected}
                                    value={option.value}
                                    onChange={this.handleOptionChange.bind(this)} />
                    </div>);
            }
        }
        return renderedOptions;
    }

    toggleExpand() {
        this.setState({
            expanded: !this.state.expanded
        });
    }

    renderSearch() {
        if (this.props.options.length > 10) {
            return (<div>
                        <Form.Control className="search"
                                      placeholder={"Search"}
                                      value={this.state.search}
                                      onChange={this.handleSearchChange.bind(this)} />
                    </div>);
        }
        return null;
    }

    render() {
        return (
            <div className={"multicheckbox-dropdown"} ref={ref => this.ref = ref}>
                <div className={"display toggler"} onClick={this.toggleExpand.bind(this)}>
                    <div className="row">
                        <div className={"col text"}>{ this.displayText() }</div>
                        <div className={"col-auto indicator"}>
                            <i className={`icon icon-angle-${this.state.expanded ? "up" : "down"}`}/>
                        </div>
                    </div>
                </div>
                <div className={`dropdown ${this.state.expanded ? "expanded" : ""}`} >

                    { this.renderSearch() }
                    <div>
                        <Form.Check type="checkbox"
                                    id={_.uniqueId("multicheckbox-")}
                                    label="All"
                                    checked={this.state.all}
                                    onChange={this.handleAllClick.bind(this)} />
                    </div>
                    { this.renderOptions() }
                </div>
            </div>
        );
    }
}


interface ActionplanFilterProps {
    onChange?: (changes: ActionplanData.FilterChanges) => void
    actionplanData: ActionplanData.ActionplanState
}

interface ActionplanFilterState {
    viewBy: viewBys,
    viewByFilter?: Array<any>, // Might convert to map
    labelGroupFilter?: Array<string>, // Might convert to map // groups are affected by other filters
    labelFilter?: Array<string>, // Might convert to map // labels are affected by other filters
    keywords: string
    expanded: boolean
}

export class ActionplanFilters extends Component<ActionplanFilterProps, ActionplanFilterState> {

    ref!: HTMLDivElement | null;
    keywordTimeout?: ReturnType<typeof setTimeout>;

    constructor(props: ActionplanFilterProps) {
        super(props);

        this.state = {
            viewBy: viewBys.ASSIGNEE,
            viewByFilter: undefined,
            labelGroupFilter: undefined,
            labelFilter: undefined,
            keywords: "",
            expanded: false
        };
        this.handleOutsideFocus = this.handleOutsideFocus.bind(this);
        this.handleEscape = this.handleEscape.bind(this);
    }
    componentDidMount() {
        document.addEventListener("click", this.handleOutsideFocus);
        document.addEventListener("keydown", this.handleEscape);
    }

    componentWillUnmount() {
        document.removeEventListener("click", this.handleOutsideFocus);
        document.removeEventListener("keydown", this.handleEscape);
        clearTimeout(this.keywordTimeout);
    }


    close() {
        this.setState({
            expanded: false
        });
    }

    handleOutsideFocus(event) {
        if (this.ref && !this.ref.contains(event.target)) {
            this.close();
        }
    }

    handleEscape(event) {
        if (event.key === "Escape") {
            this.close();
        }
    }

    selectedViewOptions() {
        return ActionplanData.getViewBys(this.props.actionplanData).map((v) => {
            return {value: v, label: v} as MultiCheckboxOption;
        });
    }

    labelOptions() {
        const options: Array<MultiCheckboxOption> = [];
        for (const label of ActionplanData.getLabels(this.props.actionplanData)) {
            options.push({
                label: label,
                value: label
            });
        }
        return options;
    }

    labelGroupOptions() {
        const options: Array<MultiCheckboxOption> = [];
        for (const labelGroup of ActionplanData.getLabelGroups(this.props.actionplanData)) {
            options.push({
                label: labelGroup,
                value: labelGroup
            });
        }
        return options;
    }

    toggleExpanded() {
        this.setState({
            expanded: !this.state.expanded
        });
    }

    emitChange() {
        if (this.props.onChange) {
            const changes: ActionplanData.FilterChanges = {
                    viewBy: this.state.viewBy,
                    viewByFilters: this.state.viewByFilter,
                    labelGroupFilters: this.state.labelGroupFilter,
                    labelFilters: this.state.labelFilter,
                    keywords: this.state.keywords
            };
            this.props.onChange(changes);
        }
    }

    handleViewByChange(event) {
        this.setState({ viewBy: event.target.value }, () => {
            this.emitChange();
        });
    }

    handleViewBySelectionChange(selections: Array<string> | undefined) {
        this.setState({ viewByFilter: selections}, () => {
            this.emitChange();
        });
    }

    handleLabelGroupSelectionChange(selections: Array<string> | undefined) {
        this.setState({labelGroupFilter: selections}, () => {
            this.emitChange();
        });
    }

    handleLabelSelectionChange(selections: Array<string> | undefined) {
        this.setState({labelFilter: selections}, () => {
            this.emitChange();
        });
    }

    handleKeywordsChange(event) {
        this.setState({keywords: event.target.value});
        clearTimeout(this.keywordTimeout);

        // const self = this;
        const emit = () => { this.emitChange(); };
        this.keywordTimeout = setTimeout(emit, 500);
    }

    handleReset(event) {
        event.preventDefault();
        this.setState({
            viewBy: viewBys.ASSIGNEE,
            viewByFilter: undefined,
            labelGroupFilter: undefined,
            labelFilter: undefined,
            keywords: "",
        }, () => {
            this.emitChange();
        });
        return false;
    }

    render() {
        return (
            <div className={"filter"} ref={ref => this.ref = ref}>
                <i className={"icon icon-filter"} onClick={this.toggleExpanded.bind(this)} />
                <div className={`D3ActionplanFilters ${this.state.expanded ? "open" : ""}`} >
                    <Form>
                        <div className={"group"}>
                            <Form.Label>Choose Your View</Form.Label>
                            <Form.Select value={this.state.viewBy}
                                onChange={this.handleViewByChange.bind(this)} >
                                <option value="" disabled selected>Select your view.</option>
                                {Object.values(viewBys).map((v) => {
                                    return <option value={v} key={v}>By {viewByLabels[v]}</option>;
                                })}
                            </Form.Select>
                        </div>
                        <div className={"group"}>
                            <Form.Label>Selected View</Form.Label>
                            <MultiCheckboxDropdown options={this.selectedViewOptions()}
                                                   onChange={this.handleViewBySelectionChange.bind(this)} />
                        </div>
                        <div className={"group"}>
                            <Form.Label>Label Group</Form.Label>
                            <MultiCheckboxDropdown options={this.labelGroupOptions()}
                                                   onChange={this.handleLabelGroupSelectionChange.bind(this)} />
                        </div>
                        <div className={"group"}>
                            <Form.Label>Label Name</Form.Label>
                            <MultiCheckboxDropdown options={this.labelOptions()}
                                                   onChange={this.handleLabelSelectionChange.bind(this)} />
                        </div>
                        <div className={"group"}>
                            <Form.Label>Keyword Search</Form.Label>
                            <Form.Control className="search"
                                          placeholder={"Search"}
                                          value={this.state.keywords}
                                          onChange={this.handleKeywordsChange.bind(this)}/>
                        </div>
                        <div className={"group filterBtns"}>
                            <button className={"btn btn-danger"} onClick={this.handleReset.bind(this)}>Reset to Default</button>
                        </div>
                    </Form>
                </div>
            </div>
        );
    }
}